/**
 * Candidates tab — Phase 4 Type B staging for NEW articles surfaced
 * by future discovery pipelines (PubMed, Semantic Scholar, PK-CAT).
 *
 * Empty until Phase 5+6 are built. UI is in place so when discovery
 * pipelines start writing to research_candidates the admin already
 * has the triage workflow ready.
 */
import React, { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Database, ExternalLink, ThumbsDown, ThumbsUp } from 'lucide-react';
import { adminFetch } from '../../../utils/adminApi';
import type { CandidateItem, PaginatedResponse } from './types';

const PAGE_SIZE = 30;

export function CandidatesTab() {
  const queryClient = useQueryClient();
  const [page, setPage] = useState(1);
  const [knowledgeType, setKnowledgeType] = useState<'all' | 'research' | 'practitioner'>('all');
  const [discoverySource, setDiscoverySource] = useState('');

  const params = new URLSearchParams({
    knowledge_type: knowledgeType,
    quality_min: '60',
    sort_by: 'discovered_desc',
    page: String(page),
    page_size: String(PAGE_SIZE),
  });
  if (discoverySource) params.set('discovery_source', discoverySource);

  const { data, isLoading } = useQuery({
    queryKey: ['rk', 'candidates', params.toString()],
    queryFn: () =>
      adminFetch<PaginatedResponse<CandidateItem>>(
        `/api/admin/researcher-knowledge/candidates?${params}`
      ),
    placeholderData: (prev) => prev,
  });

  const publish = useMutation({
    mutationFn: (id: string) =>
      adminFetch<{ status: string; new_research_source_id: string }>(
        `/api/admin/researcher-knowledge/candidates/${id}/publish`,
        { method: 'POST', body: JSON.stringify({ notes: null }) }
      ),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['rk', 'candidates'] });
      queryClient.invalidateQueries({ queryKey: ['rk', 'stats'] });
    },
  });

  const reject = useMutation({
    mutationFn: (id: string) =>
      adminFetch(
        `/api/admin/researcher-knowledge/candidates/${id}/reject`,
        { method: 'POST', body: JSON.stringify({ notes: null }) }
      ),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['rk', 'candidates'] });
      queryClient.invalidateQueries({ queryKey: ['rk', 'stats'] });
    },
  });

  return (
    <div className="space-y-4">
      <div className="rounded-lg border border-blue-200 bg-blue-50 p-3 text-sm text-blue-900">
        <div className="font-medium">Type B staging — new article candidates</div>
        <div className="mt-1 text-xs text-blue-800">
          This tab surfaces NEW articles found by automated discovery pipelines
          (PubMed, Semantic Scholar, PK-CAT) waiting for your review before
          publishing into the production library. Empty until Phases 5+6 are
          shipped — the manual triage workflow lives in the Pending Retags tab.
        </div>
      </div>

      <div className="flex flex-wrap items-center gap-3">
        <select value={knowledgeType} onChange={(e) => { setKnowledgeType(e.target.value as typeof knowledgeType); setPage(1); }}
                className="rounded-md border border-gray-300 bg-white px-3 py-2 text-sm">
          <option value="all">All knowledge types</option>
          <option value="research">Academic research</option>
          <option value="practitioner">Practitioner knowledge</option>
        </select>
        <select value={discoverySource} onChange={(e) => { setDiscoverySource(e.target.value); setPage(1); }}
                className="rounded-md border border-gray-300 bg-white px-3 py-2 text-sm">
          <option value="">All discovery sources</option>
          <option value="pubmed">PubMed</option>
          <option value="semantic_scholar">Semantic Scholar</option>
          <option value="google_scholar">Google Scholar</option>
          <option value="nkcat_scout">NK-CAT Scout</option>
          <option value="nkcat_researcher">NK-CAT Researcher</option>
          <option value="pkcat">PK-CAT</option>
          <option value="manual">Manual</option>
        </select>
      </div>

      <div className="overflow-x-auto rounded-lg border border-gray-200 bg-white shadow-sm">
        <table className="w-full text-sm">
          <thead className="bg-gray-50 text-xs uppercase text-gray-500">
            <tr>
              <th className="px-3 py-2 text-left">Title</th>
              <th className="px-3 py-2 text-left">Quality</th>
              <th className="px-3 py-2 text-left">Source</th>
              <th className="px-3 py-2 text-left">Discovered</th>
              <th className="px-3 py-2"></th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-100">
            {isLoading && (
              <tr><td colSpan={5} className="p-8 text-center text-gray-500">Loading…</td></tr>
            )}
            {data?.items.length === 0 && !isLoading && (
              <tr>
                <td colSpan={5} className="p-12 text-center">
                  <Database className="mx-auto h-8 w-8 text-gray-300" />
                  <div className="mt-2 text-gray-500">No candidates yet.</div>
                  <div className="text-xs text-gray-400">
                    Discovery pipelines (Phase 5+6) will populate this once shipped.
                  </div>
                </td>
              </tr>
            )}
            {data?.items.map((row) => (
              <tr key={row.id} className="hover:bg-gray-50">
                <td className="px-3 py-2">
                  <div className="font-medium text-gray-900">{row.title}</div>
                  <div className="text-xs text-gray-500">
                    {(row.authors || []).slice(0, 3).join(', ')} · {row.journal} · {row.publication_year}
                    {row.doi && (
                      <a href={`https://doi.org/${row.doi}`} target="_blank" rel="noopener noreferrer"
                         className="ml-2 inline-flex items-center gap-1 text-blue-600 hover:underline">
                        DOI <ExternalLink className="h-3 w-3" />
                      </a>
                    )}
                  </div>
                </td>
                <td className="px-3 py-2">
                  <span className="rounded bg-emerald-100 px-2 py-0.5 text-xs font-medium text-emerald-800">
                    {row.quality_score}
                  </span>
                </td>
                <td className="px-3 py-2 text-xs text-gray-700">
                  <div>{row.discovery_source}</div>
                  {row.discovery_query && (
                    <div className="text-gray-500 line-clamp-1">{row.discovery_query}</div>
                  )}
                </td>
                <td className="px-3 py-2 text-xs text-gray-500">
                  {new Date(row.discovered_at).toLocaleDateString()}
                </td>
                <td className="px-3 py-2 text-right">
                  <div className="flex justify-end gap-1">
                    <button
                      onClick={() => publish.mutate(row.id)}
                      disabled={publish.isPending}
                      className="inline-flex items-center gap-1 rounded bg-emerald-600 px-2 py-1 text-xs font-medium text-white hover:bg-emerald-700 disabled:opacity-50"
                    >
                      <ThumbsUp className="h-3 w-3" /> Publish
                    </button>
                    <button
                      onClick={() => reject.mutate(row.id)}
                      disabled={reject.isPending}
                      className="inline-flex items-center gap-1 rounded bg-rose-600 px-2 py-1 text-xs font-medium text-white hover:bg-rose-700 disabled:opacity-50"
                    >
                      <ThumbsDown className="h-3 w-3" /> Reject
                    </button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {data && data.total > PAGE_SIZE && (
        <div className="flex items-center justify-between text-sm text-gray-600">
          <div>
            {(data.page - 1) * data.page_size + 1}–
            {Math.min(data.page * data.page_size, data.total)} of {data.total.toLocaleString()}
          </div>
          <div className="flex gap-2">
            <button disabled={page <= 1} onClick={() => setPage((p) => Math.max(1, p - 1))}
                    className="rounded border border-gray-300 px-3 py-1 disabled:opacity-50">
              Previous
            </button>
            <span className="px-2 py-1">Page {data.page} of {data.total_pages}</span>
            <button disabled={page >= data.total_pages} onClick={() => setPage((p) => p + 1)}
                    className="rounded border border-gray-300 px-3 py-1 disabled:opacity-50">
              Next
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
